import type { IRecord, IRecordTypeDefinition } from '@platform/core/types';
import { useGetCatalogQuery, useGetRecordTypeQuery, useListRecordsQuery } from '@platform/state/api';
import type { IRelatedItem } from './IRelatedItem';
import type { IReverseRefs } from './IReverseRefs';

/** Поля, за якими прототип додає до секції записи, що посилаються на цей запис (relSecs: x.v[k] === rec.id лише для цих ключів). */
const REVERSE_KEYS: readonly string[] = ['case', 'bill', 'parent', 'mandate', 'committee', 'party', 'convocation', 'budget'];

/**
 * Записи пов'язаного типу, що посилаються на цей запис полем (прототип RELATED_DEFS: помічники мандата, членство в комітетах): ключ
 * секції — тип запису або модуль (тоді його головна таблиця); перше поле-посилання цього типу на тип запису з ключем зі списку прототипу
 * (справа, законопроєкт, батько, мандат, комітет, партія, скликання, бюджет) фільтрує список (до 25, за кодом). Повертає й ключ типу, записи якого показує секція (за ним читаються значення зв'язаних записів для фільтра секції).
 */
export function useReverseRefs(ws: string, owner: IRecordTypeDefinition, record: IRecord, key: string): IReverseRefs {
  const { data: catalog } = useGetCatalogQuery({ workspaceId: ws });
  const module = catalog?.modules.find((m) => m.key === key) ?? catalog?.modules.find((m) => m.recordTypes.some((r) => r.key === key));
  const typeKey = module ? (module.key === key ? module.recordTypes.find((r) => r.main)?.key ?? key : key) : '';
  const { data: def } = useGetRecordTypeQuery(typeKey, { skip: !typeKey });
  const field = def?.recordType.fields.find((f) => REVERSE_KEYS.includes(f.key) && (f.type === 'ref' || f.type === 'lookup') && f.ref === owner.key
    && typeKey !== owner.key);
  const { data } = useListRecordsQuery({ ws, rt: typeKey, params: { filter: `${field?.key ?? ''}:eq:${record.id}`, pageSize: 25, sort: 'code' } },
    { skip: !field || !module || !record.id });
  const items: IRelatedItem[] = (data?.items ?? []).map((r) => ({
    id: r.id, module: module?.key ?? key, recordType: typeKey, code: r.code, display: r.display, title: r.title, status: r.status, values: { ...r.ext, ...r.values },
  }));
  return { items, total: data?.total ?? 0, module: module?.key ?? key, typeKey };
}
